import { FormattedMessage } from 'react-intl';
import {
  formatCurrency,
  sumWithDeliveryFee,
  handleOrderInformation,
} from '@/service';
import React from 'react';
import { filter, find, map, remove, some } from 'lodash';
import { Order } from '@/type';
import { Link } from 'react-router-dom';
import { useInteractionState, useTranslation } from '@/hooks';
import { v4 as uuidv4 } from 'uuid';

export const ShopCartItemsTable = () => {
  const { language } = useTranslation();
  const { orders, setOrders, customer, management } = useInteractionState();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleQuantityChange = (field: Order, e: any) => {
    const currentOrders = [...(orders ?? [])];
    const found = find(currentOrders, (it: Order) => it.id === field.id);
    if (found) {
      found.quantity = Number.parseInt(e.target.value);
      setOrders?.(currentOrders);
    }
  };

  const removeOrder = (item: Order) => {
    const currentOrders = [...(orders ?? [])];
    remove(currentOrders, (it: Order) => it.id === item.id);
    setOrders?.(currentOrders);
  };

  const { price, deliveryFee, selfTaken } = sumWithDeliveryFee(
    orders,
    customer?.postal
  );
  const withRice = some(orders, (order: Order) => order.reference === '703');

  const addAdditionalRice = (event: { target: { checked: boolean } }) => {
    let currentOrders = [...(orders ?? [])];
    if (event.target.checked) {
      const rice = {
        id: uuidv4(),
        reference: '703',
        nameDe: 'Reis',
        nameEn: 'Reis',
        nameZn: '米饭',
        descriptionDe: '',
        descriptionEn: '',
        descriptionZn: '',
        quantity: 1,
        price: 1,
      };
      currentOrders = [...currentOrders, rice];
    } else {
      currentOrders = filter(
        currentOrders,
        (it: Order) => it.reference !== '703'
      );
    }
    setOrders?.([...currentOrders]);
  };

  const ordersDetail = map(orders, (it: Order) => {
    const { name, description } = handleOrderInformation(it, language);

    return (
      <tr key={it.id}>
        <td>{it.reference}</td>
        <td>{name}</td>
        <td>
          <span>{description}</span>
        </td>
        <td>{it.price}</td>
        <td>
          <div>
            <input
              type="number"
              min={1}
              style={{ width: '50px' }}
              onChange={(event) => handleQuantityChange(it, event)}
              value={it.quantity}
            />
          </div>
        </td>
        <td>{formatCurrency(it.price * it.quantity, language)}</td>
        <td>
          <Link to="#" onClick={() => removeOrder(it)}>
            <span className="glyphicon glyphicon-minus" aria-hidden="true" />
          </Link>
        </td>
      </tr>
    );
  });

  return (
    <div className="table-responsive">
      <table className="table table-bordered table-striped">
        <thead>
          <tr>
            <th>Ref</th>
            <th>
              <FormattedMessage id="app.cart.menu.name" defaultMessage="Name" />
            </th>
            <th>
              <FormattedMessage
                id="app.cart.menu.description"
                defaultMessage="Description"
              />
            </th>
            <th>
              <FormattedMessage
                id="app.cart.menu.price"
                defaultMessage="Price"
              />
            </th>
            <th>
              <FormattedMessage
                id="app.cart.menu.quantity"
                defaultMessage="Quantity"
              />
            </th>
            <th>
              <FormattedMessage
                id="app.cart.menu.total"
                defaultMessage="Total"
              />
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {ordersDetail}
          <tr>
            <td colSpan={7}>
              <div className="form-check right">
                <div className="checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={withRice}
                      onChange={addAdditionalRice}
                    />
                    <FormattedMessage
                      id="app.cart.addRice"
                      defaultMessage="Total"
                    />
                  </label>
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td colSpan={7}>
              <span className="right">
                <FormattedMessage
                  id="app.cart.menu.total"
                  defaultMessage="Total"
                />
                &nbsp;:&nbsp;
                {formatCurrency(price, language)}
              </span>
            </td>
          </tr>
          <tr>
            {!selfTaken && deliveryFee && (
              <td colSpan={7}>
                <span className="right">
                  <FormattedMessage
                    id="app.cart.menu.deliveryFee"
                    defaultMessage="Total"
                  />
                  &nbsp;:&nbsp;
                  {formatCurrency(deliveryFee, language)}
                </span>
              </td>
            )}
            {selfTaken && (
              <td colSpan={7}>
                <span className="right">
                  <strong>
                    <FormattedMessage
                      id="app.cart.menu.selfTaken"
                      defaultMessage="Total"
                    />
                  </strong>
                </span>
              </td>
            )}
          </tr>
          {deliveryFee === undefined && (
            <tr>
              <td colSpan={7}>
                <span className="right list-group-item list-group-item-info">
                  <strong>
                    <FormattedMessage
                      id="app.cart.menu.deliveryFeeContact"
                      defaultMessage="Total"
                      values={{ phone: management?.company?.telephone }}
                    />
                  </strong>
                </span>
              </td>
            </tr>
          )}
          <tr>
            <td colSpan={7}>
              {!!deliveryFee && (
                <span className="right">
                  <FormattedMessage
                    id="app.cart.menu.total.all"
                    defaultMessage="Total"
                  />
                  &nbsp;:&nbsp;
                  {formatCurrency(price + deliveryFee, language)}
                </span>
              )}
              {!deliveryFee && (
                <span className="right">
                  <FormattedMessage
                    id="app.cart.menu.total.all"
                    defaultMessage="Total"
                  />
                  &nbsp;:&nbsp;
                  {formatCurrency(price, language)}
                </span>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
